/**
 * Confirmation-overview + terminal result step bodies (027 US6 PR-3,
 * T062/T063; FR-028/FR-029; visual-map ledger rows 12-13).
 *
 * - `ConfirmationOverview` — the "Vahvista tiedot" step: label/value summary
 *   rows of the ACTIVE path's answers (radio/select answers display as their
 *   option labels, dates in the member's Finnish form, attachments by file
 *   name), then — Lakikaverille only, exactly as the payload engine applies
 *   the notices — the verbatim callback-request PREFIX (ledger row 12 as
 *   amended at r1: the PRIMARY display moved to the START of the
 *   Lakikaverille flow per the doc's Huomio 1; this overview display is the
 *   owner-approved retained slot, above the consent collapsible) and the
 *   consent collapsible whose
 *   expanded text is the FULL catalogued SUFFIX including the privacy-notice
 *   link sentence the proto omitted (ledger row 13 — content wins on text).
 *   A failed submit renders its notice HERE, above the rows, so the member
 *   can fix or retry — the answers stay reviewable and sendable (SC-004, no
 *   dead ends). Copy authority per T044: a RESOLVED failure shows the
 *   server-authored `memberMessage` verbatim; a THROWN submit shows the
 *   client-authored localized copy (mapping-refused omits the retry hint).
 * - `ThankYouStep` — the terminal success view: check-in-circle, the FR-015
 *   verbatim copy (title + server-authored member message) in a `role=status`
 *   live region. The flow hides back and offers a pinned Sulje. On a
 *   partial-attachment failure it names the not-attached files with a recovery
 *   link and takes focus there (052 US2). The attachment-upload PENDING
 *   announcement is no longer owned here: it was hoisted to a persistent
 *   wizard-level `aria-live` region (052 US3); this step keeps only a non-live
 *   visual pending note.
 *
 * Presentation-only and module-scope (R22): the flow machine owns
 * values/phase/submit state and the pinned bottom bar.
 */

import { type JSX, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';

import { type Translate, useTranslate } from '../../../i18n/useTranslate.js';
import { Dialog } from '../../../primitives/Dialog.js';
import { CheckIcon, ChevronDownIcon } from '../../../shell/icons.js';
import type {
  AttachmentRef,
  ComposeSubmitResult,
  Field,
  TopicDefinition,
} from '../MessageComposePort.js';
import type { AttachmentUploadResult } from '../useComposeAttachmentUpload.js';
import type { ComposeSubmitFailureKind } from '../useComposeSubmit.js';
import { stepColumnClasses } from './chrome.js';
import { formatIsoToFinnish } from './dateText.js';

const rowListClasses = 'm-0 flex flex-col gap-3 text-left';
const rowTermClasses = 'm-0 text-sm font-semibold text-foreground';
const rowValueClasses = 'm-0 text-sm text-muted-foreground';
// Verbatim notices: `whitespace-pre-line` preserves the blank line between
// the two consent paragraphs (the `\n\n` in the i18n copy) — the same
// treatment the inline renderer shipped for FR-007/FR-008.
const noticeClasses =
  'm-0 whitespace-pre-line rounded-md border border-border bg-muted p-4 text-left text-sm text-muted-foreground';
const consentToggleClasses =
  'flex w-full items-center justify-between gap-2 rounded-md border border-border bg-card p-4 ' +
  'text-left text-sm font-semibold text-foreground transition-colors hover:border-primary/50 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const failureBoxClasses =
  'flex flex-col gap-1 rounded-md border border-destructive bg-card p-4 text-left text-foreground';
const noticeTitleClasses = 'm-0 text-base font-semibold';
const noticeBodyClasses = 'm-0 text-sm text-muted-foreground';
const thankYouBoxClasses = 'flex flex-col items-center gap-4';
const checkCircleClasses =
  'flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary';

/** A failed submit, classified per the T044 copy-authority rules. */
export interface ConfirmationFailure {
  /** A RESOLVED failure result (server-authored memberMessage, verbatim). */
  readonly result?: ComposeSubmitResult | undefined;
  /** A THROWN submit's classification (client-authored localized copy). */
  readonly thrownKind?: ComposeSubmitFailureKind | undefined;
}

export interface ConfirmationOverviewProps {
  readonly topic: TopicDefinition;
  /** The ACTIVE path's visible fields, in path order. */
  readonly visibleFields: readonly Field[];
  readonly values: Readonly<Record<string, string>>;
  readonly attachments: readonly AttachmentRef[];
  readonly failure: ConfirmationFailure;
}

/** Display form of an answered field: option label for choices, Finnish date form for dates. */
function displayValue(field: Field, raw: string, t: Translate): string {
  if (field.type === 'radio' || field.type === 'select') {
    const option = (field.options ?? []).find((candidate) => candidate.value === raw);
    return option === undefined ? raw : t(option.label);
  }
  if (field.type === 'date') {
    return formatIsoToFinnish(raw);
  }
  return raw;
}

export function ConfirmationOverview({
  topic,
  visibleFields,
  values,
  attachments,
  failure,
}: ConfirmationOverviewProps): JSX.Element {
  const t = useTranslate();
  const consentPanelId = useId();
  const [consentOpen, setConsentOpen] = useState(false);

  const answered = visibleFields.filter((field) => (values[field.name] ?? '').trim().length > 0);
  const isLakikaverille = topic.serviceLine === 'viesti_lakikaverille';
  const failureResult = failure.result?.outcome === 'failure' ? failure.result : undefined;

  return (
    <div className={stepColumnClasses}>
      <Dialog.Title size="wizard">{t('compose.wizard.confirm.title')}</Dialog.Title>

      {failureResult !== undefined ? (
        <div role="alert" className={failureBoxClasses}>
          <p className={noticeTitleClasses}>{t('compose.notification.failureTitle')}</p>
          <p className={noticeBodyClasses}>{failureResult.memberMessage}</p>
        </div>
      ) : failure.thrownKind !== undefined ? (
        <div role="alert" className={failureBoxClasses}>
          <p className={noticeTitleClasses}>{t('compose.notification.failureTitle')}</p>
          <p className={noticeBodyClasses}>
            {t(
              failure.thrownKind === 'mapping_refused'
                ? 'compose.notification.mappingRefusedBody'
                : 'compose.notification.transientFailureBody',
            )}
          </p>
        </div>
      ) : null}

      <dl className={rowListClasses}>
        {answered.map((field) => (
          <div key={field.name}>
            <dt className={rowTermClasses}>{t(field.label)}</dt>
            <dd className={`${rowValueClasses} m-0`}>
              {displayValue(field, (values[field.name] ?? '').trim(), t)}
            </dd>
          </div>
        ))}
        {attachments.length > 0 ? (
          <div>
            <dt className={rowTermClasses}>{t('compose.wizard.confirm.attachments')}</dt>
            <dd className={`${rowValueClasses} m-0`}>
              {attachments.map((attachment) => attachment.fileName).join(', ')}
            </dd>
          </div>
        ) : null}
      </dl>

      {isLakikaverille ? (
        <>
          {/* Verbatim callback-request PREFIX (FR-007 display slot, ledger
              row 12): ABOVE the consent collapsible. */}
          <p className={noticeClasses}>{t('compose.prefix')}</p>
          <button
            type="button"
            className={consentToggleClasses}
            aria-expanded={consentOpen}
            aria-controls={consentPanelId}
            onClick={() => setConsentOpen((open) => !open)}
          >
            {t('compose.wizard.confirm.consentToggle')}
            <ChevronDownIcon
              aria-hidden="true"
              className={`h-4 w-4 shrink-0 transition-transform ${consentOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {consentOpen ? (
            <p id={consentPanelId} className={noticeClasses}>
              {t('compose.suffix')}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export interface ThankYouStepProps {
  /** The server-authored member-facing success copy, verbatim (FR-015). */
  readonly memberMessage: string;
  /** 051 US1: true while the real attachment bytes upload to the created case. */
  readonly attachmentsPending?: boolean | undefined;
  /**
   * 051 US2: the per-file attachment transfer results, once the upload pass
   * has finished. `undefined` when the message carried no attachments.
   */
  readonly attachmentResults?: readonly AttachmentUploadResult[] | undefined;
  /** 051 US2: link to the created conversation, for adding a file that failed to attach. */
  readonly conversationHref?: string | undefined;
}

const recoveryLinkClasses = 'font-medium text-primary underline';
const failedListClasses = 'm-0 list-disc pl-5 text-left text-sm text-muted-foreground';

/**
 * thank-you idiom: check-in-circle + the FR-015 verbatim copy, plus the 051
 * attachment transfer status: a pending note while bytes upload, then — only
 * if one or more files did not attach — an honest per-file notice naming them
 * and a link to add them in the created conversation (US2, no fabricated
 * success). When every attachment attached (or there were none) no extra
 * notice is shown.
 */
export function ThankYouStep({
  memberMessage,
  attachmentsPending,
  attachmentResults,
  conversationHref,
}: ThankYouStepProps): JSX.Element {
  const t = useTranslate();
  const failed = (attachmentResults ?? []).filter((result) => result.outcome !== 'ok');
  // 052 US2: whether the partial-failure notice (with its recovery link) is now
  // shown — a primitive so the focus effect below keys on the pending→done
  // transition, not on the recomputed `failed` array identity.
  const showFailure = attachmentsPending !== true && failed.length > 0;
  // 051: move focus to the success confirmation on mount so it never drops to
  // <body> during the attachment-upload pending window (the pinned Close button
  // — the usual focus target — is withheld while pending). WCAG 2.4.3. When the
  // upload finishes and everything attached, the Close button mounts with its
  // own focusOnMount and takes over. (App-wide route-change focus policy remains
  // a separate follow-up.)
  const headingRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  // 052 US2: on a partial-attachment failure, move focus to the recovery link
  // when the failure notice appears at the pending→done transition — NOT to the
  // `role="alert"` box itself (focusing an alert node double-announces), and NOT
  // to the pinned Close (its `focusOnMount` is suppressed on this path, so an
  // immediate Enter cannot discard the wizard before the member reaches
  // recovery). WCAG 2.4.3. Declared after the mount-focus effect so, in the rare
  // case the failure is already present at mount, this focus wins.
  const recoveryRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    if (showFailure) {
      recoveryRef.current?.focus();
    }
  }, [showFailure]);
  return (
    <div className={stepColumnClasses}>
      {/* tabIndex=-1: a programmatic focus target (not tabbable) so terminal
          focus lands on the confirmation, never <body>, while the Close button
          is withheld during the attachment-upload window (WCAG 2.4.3). The
          focus target is a plain wrapper — NOT itself role="status" — so the
          focus read and the inner live region cannot double-announce the
          success copy (a focused status node would be read twice). */}
      <div ref={headingRef} tabIndex={-1}>
        <div role="status" className={thankYouBoxClasses}>
          <span aria-hidden="true" className={checkCircleClasses}>
            <CheckIcon className="h-8 w-8" />
          </span>
          <Dialog.Title size="wizard">{t('compose.notification.successTitle')}</Dialog.Title>
          <p className={noticeBodyClasses}>{memberMessage}</p>
        </div>
      </div>
      {/* 051 pending, 052 US3: the VISIBLE pending note for sighted members. The
          ANNOUNCING live region was hoisted to the persistent wizard level
          (`WizardQuestionFlow`) so it pre-exists (empty) across the
          confirm→pending→result transition and reliably announces the mutated
          text — rather than mounting together with its text as it did here (which
          VoiceOver/NVDA announced unreliably). This note is therefore purely
          visual now: no `aria-live`, to avoid a second competing announcement. */}
      {attachmentsPending === true ? (
        <p className={noticeBodyClasses}>{t('compose.attachment.uploading')}</p>
      ) : null}
      {attachmentsPending !== true && failed.length > 0 ? (
        <div role="alert" className={failureBoxClasses}>
          <p className={noticeTitleClasses}>{t('compose.attachment.partialFailureTitle')}</p>
          <p className={noticeBodyClasses}>{t('compose.attachment.partialFailureBody')}</p>
          <ul className={failedListClasses}>
            {failed.map((result) => (
              <li key={result.fileName}>{result.fileName}</li>
            ))}
          </ul>
          {conversationHref !== undefined ? (
            <Link ref={recoveryRef} to={conversationHref} className={recoveryLinkClasses}>
              {t('compose.attachment.recoveryLink')}
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
